import clsx from 'clsx';
import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { useEscape } from '@/lib/hooks/useEscape';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { ns, useT } from '@/i18n';
import { IconButton } from '../IconButton/IconButton';
import styles from './Sheet.module.css';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: ReactNode;
  /** Sticky footer (summary + primary action). */
  footer?: ReactNode;
  children: ReactNode;
  /** Experiences universe look (warm paper, saffron actions). */
  tone?: 'veh' | 'exp';
  /** Class for the body area, to lay out its content. */
  bodyClassName?: string;
  /** Width on desktop: `lg` 960 (search), `md` 640, `sm` 480. */
  size?: 'lg' | 'md' | 'sm';
}

/**
 * Dialog sheet (`.so`): a centred card on desktop, a full-height sheet sliding up on mobile.
 * Scrim click and Esc close it; focus is trapped and restored; page scroll is locked.
 */
export function Sheet({
  open,
  onClose,
  title,
  eyebrow,
  footer,
  children,
  tone = 'veh',
  bodyClassName,
  size = 'lg',
}: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const t = useT(ns.common);
  useFocusTrap(ref, open);
  useBodyScrollLock(open);
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div data-universe={tone}>
      <button type="button" className={styles.scrim} aria-label={t('close')} tabIndex={-1} onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(styles.so, styles[size], tone === 'exp' && styles.exp)}
      >
        <div className={styles.head}>
          <div>
            {eyebrow && <span className="t-eyebrow">{eyebrow}</span>}
            <h2 id={titleId} className={clsx('t-display t-h3', styles.title)}>
              {title}
            </h2>
          </div>
          <IconButton icon="close" label={t('close')} iconSize={18} strokeWidth={2.2} onClick={onClose} />
        </div>
        <div className={clsx(styles.body, bodyClassName)}>{children}</div>
        {footer && <div className={styles.foot}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
